import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, combineLatest, Subject, zip } from 'rxjs';
import { map, filter, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ArchiefService } from '../archief.service';
import { IZittingDb } from '../../shared/offline/cobradb';
import { IHistoryItem } from '../../shared/state';
import { AppService } from '../../shared/app.service';

import { GlobalDataService, ICodeIdOmschrijving } from 'src/app/shared/global-data.service';
import { createCmdFactory } from '@cevi/rxjscommands';
import { ZittingListComponent } from '../../shared/zitting-list/zitting-list.component';
import { FormsModule } from '@angular/forms';
import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';


const aantalJaren = 8;

interface IArchiefVm {
  activeJaar: number;
  jaren: number[];
  zittingen: IZittingDb[];
  start?: number;
  soortenZitting: ICodeIdOmschrijving[];
  soortZittingId?: number;
}
const emptyVm = {
  activeJaar: null,
  jaren: [],
  zittingen: [],
  soortenZitting: []
};

@Component({
    selector: 'app-zitting-archief',
    templateUrl: './zitting-archief.component.html',
    styleUrls: ['./zitting-archief.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, FormsModule, NgFor, NgClass, ZittingListComponent, AsyncPipe],
    providers:[ArchiefService]
})
export class ZittingArchiefComponent implements OnInit {

  vm$: Observable<IArchiefVm>;
  vm: IArchiefVm;

  historyItem: IHistoryItem;
  soortZittingCmd = new Subject<number>();
  soortZittingId$: Observable<number>;
  constructor(private route: ActivatedRoute, private archiefService: ArchiefService,
    private globalDataService: GlobalDataService,
    public appService: AppService) {

    this.appService.routing.createCurrentHistoryItem('Archief');
    const cmdFactory = createCmdFactory<IArchiefVm>();

    cmdFactory.addMapAction(this.soortZittingFromAppDataAction$, 'soortzittingfromappdata');
    cmdFactory.addMapAction(this.zittingenFromQueryParamsAction$, 'zittingenfromqueryparams');
    cmdFactory.addMapAction(this.startJaarFromQueryParamsAction$, 'startjaarfromqueryparams');
    cmdFactory.addMapAction(this.soortZittingIdFromSelectAction$, 'selectSoortZitting');
    cmdFactory.addMapAction(this.reloadFromRefreshAction$, 'reloadFromRefresh');
    const start = new Date().getFullYear();
    this.vm$ = cmdFactory.merge({ ...emptyVm, jaren: this.getJaren(start), start, soortZittingId:null });
  }

  private soortZittingFromAppDataAction$ = this.globalDataService.getSoortenZitting().pipe(
    map(soortenZitting => (vm: IArchiefVm) => ({ ...vm, soortenZitting }))
  );

  private queryParams$ = this.route.queryParamMap.pipe(
    map(queryParamMap => ({ soortZittingId: parseInt(queryParamMap.get('soortZittingId')) || null, jaar: +queryParamMap.get('jaar'), t: queryParamMap.get('t') })),
    filter(params => !!params.jaar),
    distinctUntilChanged((prev, curr) => prev.jaar === curr.jaar && prev.soortZittingId === curr.soortZittingId),
  );
  private zittingen$ = this.queryParams$.pipe(
    switchMap(params => this.archiefService.getZittingen(params.jaar, params.soortZittingId))
  );

  private zittingenFromQueryParamsAction$ = zip(this.queryParams$, this.zittingen$).pipe(
    map(([queryParams, zittingen]) => ({ zittingen, activeJaar: queryParams.jaar, soortZittingId: queryParams.soortZittingId })),
    map(value => (vm: IArchiefVm) => {
      this.appService.routing.setCurrentTitle(`Archief ${value.activeJaar}`);
      return { ...vm, ...value };
    })
  );

  private startJaarFromQueryParamsAction$ = this.route.queryParamMap.pipe(
    map(queryParamMap => +queryParamMap.get('start')),
    filter(start => !!start),
    distinctUntilChanged(),
    map(start => (vm: IArchiefVm) => ({ ...vm, start, jaren: this.getJaren(start) }))
  );

  private soortZittingIdFromSelectAction$ = this.soortZittingCmd.pipe(
    map(soortZittingId => (vm: IArchiefVm): IArchiefVm => {
      this.appService.routing.navigateToReplaceHistory(`Archief ${vm.activeJaar}`, ['zittingarchief'], { queryParams: { start: vm.start, jaar: vm.activeJaar, soortZittingId } });
      return vm;
    })
  );

  private reloadFromRefreshAction$ = this.appService.refresh$.pipe(
    map(_ => (vm: IArchiefVm): IArchiefVm => {
      const timestamp = new Date().getTime();
      this.appService.routing.navigateToReplaceHistory(`Archief ${vm.activeJaar}`, ['zittingarchief'], { queryParams: { t: timestamp }, queryParamsHandling: 'merge' });
      return vm;
    })
  );

  getJaren(start: number) {
    start = start || new Date().getFullYear();
    return Array.from(Array(aantalJaren).keys()).map(n => start + n + 1 - aantalJaren);
  }

  ngOnInit() {
  }

  navigateToPrevious(vm: IArchiefVm) {
    const start = vm.start - aantalJaren;
    this.appService.routing.navigateToReplaceHistory(`Archief ${vm.activeJaar}`, ['zittingarchief'], { queryParams: { start, soortZittingId: vm.soortZittingId }, queryParamsHandling: 'merge' });
  }
  navigateToNext(vm: IArchiefVm) {
    if (vm.start + aantalJaren - 1 >= new Date().getFullYear()) {
      return;
    }
    const start = vm.start + aantalJaren;
    this.appService.routing.navigateToReplaceHistory(`Archief ${vm.activeJaar}`, ['zittingarchief'], { queryParams: { start }, queryParamsHandling: 'merge' });
  }

  selectJaar(jaar: number) {
    this.appService.routing.navigateToReplaceHistory(`Archief ${jaar}`, ['zittingarchief'], { queryParams: { jaar }, queryParamsHandling: 'merge' });
  }

}
